/**
 * Poll one asynchronous onboarding phase until it resolves, or until the user
 * has waited longer than the flow is willing to make them wait.
 *
 * The generation behind these phases runs on infrastructure built for weekly
 * batches, so "it finished" is not a safe assumption. Every phase therefore has
 * a client deadline after which the caller renders generic copy and lets the
 * user carry on — the escape hatch is the feature, not a fallback.
 */

import { useFetch } from "~vue/api";
import { onScopeDispose, type Ref, ref, type ShallowRef, shallowRef } from "vue";

type PollOutcome = "idle" | "polling" | "resolved" | "timed_out" | "error";

/*
 * Readings are persisted one at a time as they finish, so the interval is the
 * delay between a reading existing and the user seeing it — not just how often
 * the run is checked. A whole run is around ten seconds, which a two-second poll
 * padded noticeably.
 */
const DEFAULT_INTERVAL_MS = 1000;
const DEFAULT_DEADLINE_MS = 75000;

interface PollOptions<T> {
    /** True once the payload is terminal and polling should stop. */
    isResolved: (payload: T) => boolean;
    /** True when the phase has failed and there is nothing left to wait for. */
    isFailed?: (payload: T) => boolean;
    intervalMs?: number;
    /** Null keeps polling until the server reports a terminal state. */
    deadlineMs?: number | null;
}

/** Annotated rather than inferred: the inferred shape is not nameable across package boundaries. */
export interface PollingPhase<T> {
    data: ShallowRef<T | null>;
    outcome: Ref<PollOutcome>;
    start: () => void;
    stop: () => void;
}

export function usePollingPhase<T>(url: string, options: PollOptions<T>): PollingPhase<T> {
    const { isResolved, isFailed, intervalMs = DEFAULT_INTERVAL_MS, deadlineMs = DEFAULT_DEADLINE_MS } = options;

    const { execute, abort, data: fetched, error } = useFetch<T>(url, { immediate: false }).get().json<T>();

    const data = shallowRef<T | null>(null);
    const outcome = ref<PollOutcome>("idle");
    let timer: ReturnType<typeof setTimeout> | null = null;
    let deadlineTimer: ReturnType<typeof setTimeout> | null = null;
    let generation = 0;

    function stop(): void {
        generation += 1;
        if (timer !== null) {
            clearTimeout(timer);
            timer = null;
        }
        if (deadlineTimer !== null) {
            clearTimeout(deadlineTimer);
            deadlineTimer = null;
        }
        abort();
    }

    async function tick(currentGeneration: number): Promise<void> {
        await execute();
        if (currentGeneration !== generation) {
            return;
        }
        const payload = error.value ? null : (fetched.value as T | null);

        // A single failed poll is not a failed phase; the deadline below decides
        // when to give up.
        if (payload !== null) {
            data.value = payload;
            if (isFailed?.(payload)) {
                stop();
                outcome.value = "error";
                return;
            }
            if (isResolved(payload)) {
                stop();
                outcome.value = "resolved";
                return;
            }
        }

        timer = setTimeout(() => {
            void tick(currentGeneration);
        }, intervalMs);
    }

    function start(): void {
        stop();
        data.value = null;
        outcome.value = "polling";
        // A stalled HTTP request must not hold the escape hatch past the deadline.
        if (deadlineMs !== null) {
            deadlineTimer = setTimeout(() => {
                stop();
                outcome.value = "timed_out";
            }, deadlineMs);
        }
        void tick(generation);
    }

    onScopeDispose(stop);

    // Neither ref is wrapped in `readonly`: `data` is handed straight to component
    // props, which a deeply-readonly payload will not assign to, and wrapping only
    // `outcome` leaves the composable with an unnameable inferred return type.
    return { data, outcome, start, stop };
}
